import { useState, useRef, useEffect, useMemo } from "react";
import type { Comment } from "@/services/api/comments";
import { CommentItem } from "./CommentItem";
import { CommentInput } from "./CommentInput";

interface CommentsSectionProps {
  comments: Comment[];
  onCreateComment: (text: string, fileId: string) => void;
  onUpdateComment: (id: string | number, text: string) => void;
  onDeleteComment: (id: string | number) => void;
  isCreating?: boolean;
  isUpdating?: boolean;
  fileId: string;
}

export function CommentsSection({
  comments,
  onCreateComment,
  onUpdateComment,
  onDeleteComment,
  isCreating = false,
  isUpdating = false,
  fileId,
}: CommentsSectionProps) {
  const [editingCommentId, setEditingCommentId] = useState<
    string | number | null
  >(null);
  const [editValue, setEditValue] = useState("");
  const [newCommentIds, setNewCommentIds] = useState<Set<string | number>>(
    new Set()
  );
  const commentInputRef = useRef<HTMLDivElement>(null);
  const commentsContainerRef = useRef<HTMLDivElement>(null);
  const prevCommentsLengthRef = useRef(comments.length);
  const prevIsCreatingRef = useRef(isCreating);
  const prevCommentIdsRef = useRef<Set<string | number>>(new Set());

  const sortedComments = useMemo(() => {
    return [...comments].sort((a, b) => {
      if (!a.createdAt && !b.createdAt) return 0;
      if (!a.createdAt) return -1;
      if (!b.createdAt) return 1;

      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }, [comments]);

  useEffect(() => {
    const currentCommentIds = new Set(
      sortedComments.map((comment) => comment.id)
    );
    const newIds = new Set<string | number>();

    currentCommentIds.forEach((id) => {
      if (!prevCommentIdsRef.current.has(id)) {
        newIds.add(id);
      }
    });

    if (newIds.size > 0) {
      setNewCommentIds(newIds);
      const timer = setTimeout(() => {
        setNewCommentIds((prev) => {
          const updated = new Set(prev);
          newIds.forEach((id) => updated.delete(id));
          return updated;
        });
      }, 500);

      return () => clearTimeout(timer);
    }

    prevCommentIdsRef.current = currentCommentIds;
  }, [sortedComments]);

  useEffect(() => {
    const commentAdded = sortedComments.length > prevCommentsLengthRef.current;
    const creationFinished = prevIsCreatingRef.current && !isCreating;

    if (commentAdded || creationFinished) {
      setTimeout(() => {
        let scrollableParent: HTMLElement | null =
          commentsContainerRef.current?.parentElement || null;

        while (scrollableParent) {
          const style = window.getComputedStyle(scrollableParent);
          const isScrollable =
            scrollableParent.scrollHeight > scrollableParent.clientHeight &&
            (style.overflowY === "auto" ||
              style.overflowY === "scroll" ||
              scrollableParent.classList.contains("overflow-y-auto"));

          if (isScrollable) {
            break;
          }
          scrollableParent = scrollableParent.parentElement;
        }

        if (scrollableParent) {
          scrollableParent.scrollTo({
            top: scrollableParent.scrollHeight,
            behavior: "smooth",
          });
        }

        setTimeout(() => {
          const input = commentInputRef.current?.querySelector("input");
          input?.focus();
        }, 300);
      }, 150);
    }

    prevCommentsLengthRef.current = sortedComments.length;
    prevIsCreatingRef.current = isCreating;
  }, [sortedComments.length, isCreating]);

  const handleStartEdit = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditValue(comment.text);
  };

  const handleUpdate = () => {
    if (editingCommentId && editValue.trim() && !isUpdating) {
      onUpdateComment(editingCommentId, editValue.trim());
      setEditingCommentId(null);
      setEditValue("");
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditValue("");
  };

  const handleSendComment = (text: string) => {
    onCreateComment(text, fileId);
  };

  return (
    <div className="flex flex-col border-t border-grid-200 relative">
      <div ref={commentsContainerRef} className="p-4 space-y-4 pb-4">
        {sortedComments.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-ink-400">No comments yet.</p>
          </div>
        ) : (
          sortedComments.map((comment) => {
            const isNew = newCommentIds.has(comment.id);
            return (
              <div
                key={comment.id}
                className={isNew ? "animate-fade-in-up" : ""}
              >
                <CommentItem
                  comment={comment}
                  isEditing={editingCommentId === comment.id}
                  editValue={editValue}
                  isUpdating={isUpdating}
                  onStartEdit={handleStartEdit}
                  onUpdate={handleUpdate}
                  onCancelEdit={handleCancelEdit}
                  onDelete={onDeleteComment}
                  onEditValueChange={setEditValue}
                />
              </div>
            );
          })
        )}
      </div>

      <div ref={commentInputRef} className="sticky bottom-0 bg-white z-10">
        <CommentInput
          onSendComment={handleSendComment}
          isCreating={isCreating}
        />
      </div>
    </div>
  );
}
